import {
	formatBirthWithAgeDisplay,
	formatDateDisplay,
} from '@/utils/participantDetailDisplay';
import { PATIENT_GENDER_LABELS } from '@/constants/patient';

export type ProntuarioPatientSummary = {
	fullName: string;
	birthDate: string;
	age: string;
	gender: string;
	phone: string;
	occupation: string;
};

const EMPTY_VALUE = '—';

export type SessionParticipantTarget = {
	patientId: string | null;
	userId: string | null;
};

const resolvePrimaryParticipant = (session?: Sessions.Session | null) => {
	if (!session) return null;

	const participant =
		session.participants?.find(
			(entry) =>
				entry.entity_type === 'PATIENT' || entry.entity_type === 'USER',
		) ?? session.participants?.[0];

	return participant ?? null;
};

export const resolveSessionParticipantTarget = (
	session?: Sessions.Session | null,
): SessionParticipantTarget => {
	const participant = resolvePrimaryParticipant(session);

	if (participant?.patient_id || participant?.patient?.id) {
		return {
			patientId:
				participant.patient_id ?? participant.patient?.id ?? null,
			userId: null,
		};
	}

	if (participant?.user_id) {
		return { patientId: null, userId: participant.user_id };
	}

	if (session?.user_id) {
		return { patientId: null, userId: session.user_id };
	}

	return { patientId: null, userId: null };
};

const buildSummaryFromSession = (
	session?: Sessions.Session | null,
): ProntuarioPatientSummary => {
	const participant = resolvePrimaryParticipant(session);

	if (participant?.patient) {
		const { patient } = participant;
		return {
			fullName: patient.full_name?.trim() || EMPTY_VALUE,
			birthDate:
				formatBirthWithAgeDisplay(patient.birth_date) ??
				formatDateDisplay(patient.birth_date) ??
				EMPTY_VALUE,
			age: EMPTY_VALUE,
			gender: patient.gender
				? PATIENT_GENDER_LABELS[patient.gender] ?? patient.gender
				: EMPTY_VALUE,
			phone: patient.phone?.trim() || EMPTY_VALUE,
			occupation: patient.profession?.trim() || EMPTY_VALUE,
		};
	}

	if (participant?.user) {
		const { user } = participant;
		const fullName = [user.first_name, user.last_name]
			.filter(Boolean)
			.join(' ')
			.trim();

		return {
			fullName: fullName || EMPTY_VALUE,
			birthDate:
				formatBirthWithAgeDisplay(user.birthday) ??
				formatDateDisplay(user.birthday) ??
				EMPTY_VALUE,
			age: EMPTY_VALUE,
			gender: EMPTY_VALUE,
			phone: user.phone_number?.trim() || EMPTY_VALUE,
			occupation: EMPTY_VALUE,
		};
	}

	const sessionName = [session?.first_name, session?.last_name]
		.filter(Boolean)
		.join(' ')
		.trim();

	return {
		fullName: sessionName || 'Paciente da consulta',
		birthDate: EMPTY_VALUE,
		age: EMPTY_VALUE,
		gender: EMPTY_VALUE,
		phone: EMPTY_VALUE,
		occupation: EMPTY_VALUE,
	};
};

export { buildSummaryFromSession };
